import React from "react";
import { injectStyles } from "./inject-styles";

/* ------------------------------------------------------------------ */
/*  Skeleton                                                          */
/* ------------------------------------------------------------------ */

export type SkeletonVariant = "text" | "circle" | "rect";

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: SkeletonVariant;
  /** Width (CSS string). Text defaults to 100%, circle to size, rect to 100%. */
  width?: string | number;
  /** Height (CSS string). Text defaults to 1em, circle to size, rect to 100px. */
  height?: string | number;
  /** Shortcut for circle/square size. Defaults to 40. */
  size?: number;
  /** Number of text lines to render. Only for variant="text". */
  lines?: number;
}

const css = /* css */ `
  @keyframes sf-skeleton-pulse {
    0%   { opacity: 1; }
    50%  { opacity: 0.4; }
    100% { opacity: 1; }
  }

  .sf-skeleton {
    background: var(--sf-gray-200);
    animation: sf-skeleton-pulse 1.5s ease-in-out infinite;
  }

  .sf-skeleton--text {
    border-radius: var(--sf-radius-sm);
    height: 1em;
    width: 100%;
  }

  .sf-skeleton--circle {
    border-radius: 50%;
    flex-shrink: 0;
  }

  .sf-skeleton--rect {
    border-radius: var(--sf-radius-md);
    width: 100%;
  }

  .sf-skeleton-lines {
    display: flex;
    flex-direction: column;
    gap: var(--sf-space-2);
  }
`;

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  (
    {
      variant = "text",
      width,
      height,
      size = 40,
      lines = 1,
      className,
      style,
      ...rest
    },
    ref,
  ) => {
    injectStyles("sf-skeleton", css);

    const getStyle = (lastLine = false): React.CSSProperties => {
      const base: React.CSSProperties = { ...style };

      if (variant === "text") {
        if (width) base.width = typeof width === "number" ? `${width}px` : width;
        if (height) base.height = typeof height === "number" ? `${height}px` : height;
        // Make the last line in a multi-line group shorter for realism.
        if (lastLine && lines > 1 && !width) base.width = "75%";
      } else if (variant === "circle") {
        const s = width ?? size;
        base.width = typeof s === "number" ? `${s}px` : s;
        base.height = typeof s === "number" ? `${s}px` : s;
      } else {
        // rect
        if (width) base.width = typeof width === "number" ? `${width}px` : width;
        base.height =
          height != null
            ? typeof height === "number"
              ? `${height}px`
              : height
            : "100px";
      }

      return base;
    };

    const classes = [
      "sf-skeleton",
      `sf-skeleton--${variant}`,
      className ?? "",
    ]
      .filter(Boolean)
      .join(" ");

    if (variant === "text" && lines > 1) {
      return (
        <div className="sf-skeleton-lines" role="status" aria-label="Loading">
          {Array.from({ length: lines }, (_, i) => (
            <div
              key={i}
              ref={i === 0 ? ref : undefined}
              className={classes}
              style={getStyle(i === lines - 1)}
              aria-hidden="true"
              {...(i === 0 ? rest : {})}
            />
          ))}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={classes}
        style={getStyle(false)}
        role="status"
        aria-label="Loading"
        {...rest}
      />
    );
  },
);

Skeleton.displayName = "Skeleton";
export { Skeleton };
